import { inject, Injectable } from '@angular/core';
import { GrowlerService } from '@app/components/core/growler/service/growler.service';
import { GrowlerPendingService } from '@app/service/base-growler/growler-pending.service';
import { angularjs } from '@app/service/migration-helpers/angularjs.injection';
import type { IContentStore } from '@app/interface/content/content-store.interface';
import { GROWLER_KEYS } from './growler-content-keys';

interface AddToCartPending {
  names: string[];
  items: string[];
}

@Injectable({ providedIn: 'root' })
export class AddToCartGrowlerService extends GrowlerPendingService<AddToCartPending> {
  private readonly growlerService = inject(GrowlerService);
  private readonly contentStore = inject<IContentStore>(angularjs.ContentStore);

  showForStudents(studentNames: string[], items: string[]): void {
    const headline = this.formatHeadline(studentNames);
    const formattedItems = items.map((item) => this.formatItem(item));
    this.growlerService.success({
      title: headline,
      items: formattedItems,
      closeable: true,
      autoDismiss: true,
      growlerStyle: 'floating'
    });
  }

  setPending(names: string[], items: string[]): void {
    this.setPendingData({ names, items });
  }

  protected showPending({ names, items }: AddToCartPending): void {
    this.showForStudents(names, items);
  }

  formatHeadline(names: string[]): string {
    if (names.length === 0) return '';
    const nameList = this.formatNameList(names);
    return this.contentStore.translate(GROWLER_KEYS.ADDED_TO_CART_HEADLINE, { names: nameList });
  }

  formatNameList(names: string[]): string {
    const capped = names.slice(0, 4);
    if (capped.length === 1) return capped[0];
    const last = capped[capped.length - 1];
    const rest = capped.slice(0, -1);
    return `${rest.join(', ')} & ${last}`;
  }

  private formatItem(name: string): string {
    return this.contentStore.translate(GROWLER_KEYS.ITEM_LINE, { quantity: '1', itemName: name });
  }
}
